import { FormEvent, useEffect, useMemo, useRef, useState } from 'react'
import { ClinicalInput, Diagnosis, UrineKetones, evaluateClinicalState } from './clinical/engine'
import { Episode, createEpisode, makeObservation } from './clinical/episode'
import EpisodePanel from './EpisodePanel'
import ObservationCorrectionPanel from './ObservationCorrectionPanel'
import GuidedWorkflowPanel from './GuidedWorkflowPanel'
import DemoScenarioPanel from './DemoScenarioPanel'
import CaseArchivePanel from './CaseArchivePanel'
import { RuleReferences, SourcesMethodologyPanel } from './ReferenceLinks'
import {
  listEpisodes,
  loadSelectedEpisode,
  markEpisodeSelected,
  markNewCaseSelected,
  saveEpisode,
  saveLocalEvaluation
} from './storage'
import type { StoredEpisode } from './storage'

const diagnosisLabels: Record<Diagnosis, string> = {
  DKA: 'Chetoacidosi diabetica (DKA)',
  EUGLYCEMIC_DKA: 'DKA euglicemica',
  HHS: 'Stato iperosmolare iperglicemico (HHS)',
  MIXED_DKA_HHS: 'Forma mista DKA/HHS',
  UNCLASSIFIED: 'Quadro non classificato'
}

const severityLabels = {
  mild: 'Lieve', moderate: 'Moderata', severe: 'Severa', indeterminate: 'Non determinabile'
} as const

const alertRank = { critical: 0, warning: 1, info: 2 } as const

const num = (value: FormDataEntryValue | null): number | undefined => {
  if (typeof value !== 'string' || value.trim() === '') return undefined
  const parsed = Number(value.replace(',', '.'))
  return Number.isFinite(parsed) ? parsed : undefined
}

const urineKetones = (value: FormDataEntryValue | null): UrineKetones | undefined => {
  const parsed = num(value)
  return parsed === 0 || parsed === 0.5 || parsed === 1 || parsed === 2 || parsed === 3 || parsed === 4 ? parsed : undefined
}

const hasAnalysisData = (fd: FormData) => {
  const hasNumber = (name: string) => num(fd.get(name)) !== undefined
  return hasNumber('glucoseMgDl') && hasNumber('sodiumMmolL') && hasNumber('potassiumMmolL') &&
    hasNumber('bicarbonateMmolL') && hasNumber('ph')
}

function Field({ name, label, unit, step = 'any', needed = false, optional = false }: {
  name: string; label: string; unit?: string; step?: string; needed?: boolean; optional?: boolean
}) {
  return (
    <label className="field">
      <span className="field-label"><span>{label}{needed && <sup>*</sup>}</span>{optional && <small>opz.</small>}</span>
      <div className="input-wrap"><input inputMode="decimal" name={name} type="number" step={step} />{unit && <small>{unit}</small>}</div>
    </label>
  )
}

function CriterionGroup({ title, criteria }: { title: string; criteria: Array<{ label: string; met: boolean | null; detail: string }> }) {
  return (
    <div>
      <h3>{title}</h3>
      <div className={`criteria-row ${criteria.length === 4 ? 'four' : ''}`}>
        {criteria.map((criterion) => (
          <div className={`criterion ${criterion.met === true ? 'yes' : criterion.met === false ? 'no' : 'unknown'}`} key={criterion.label}>
            <strong>{criterion.label}</strong><span>{criterion.met === true ? '✓' : criterion.met === false ? '×' : '?'}</span><small>{criterion.detail}</small>
          </div>
        ))}
      </div>
    </div>
  )
}

export default function App() {
  const formRef = useRef<HTMLFormElement>(null)
  const resultRef = useRef<HTMLElement>(null)
  const episodeRef = useRef<HTMLDivElement>(null)
  const [input, setInput] = useState<ClinicalInput>({ knownDiabetes: false })
  const [canAnalyze, setCanAnalyze] = useState(false)
  const [hasAnalyzed, setHasAnalyzed] = useState(false)
  const [stale, setStale] = useState(false)
  const [saved, setSaved] = useState(false)
  const [episode, setEpisode] = useState<Episode | null>(null)
  const [episodes, setEpisodes] = useState<StoredEpisode[]>([])
  const [archiveOpen, setArchiveOpen] = useState(false)
  const [episodeLoaded, setEpisodeLoaded] = useState(false)
  const [restored, setRestored] = useState(false)
  const [storageError, setStorageError] = useState('')
  const result = useMemo(() => evaluateClinicalState(input), [input])
  const sortedAlerts = useMemo(() => [...result.alerts].sort((a, b) => alertRank[a.level] - alertRank[b.level]), [result.alerts])
  const resultTitle = hasAnalyzed
    ? (result.diagnosis === 'UNCLASSIFIED' && result.provisionalPattern ? result.provisionalPattern.label : diagnosisLabels[result.diagnosis])
    : 'In attesa dei dati'
  const resultStatus = !hasAnalyzed
    ? 'IN ATTESA'
    : result.diagnosis !== 'UNCLASSIFIED'
      ? 'CLASSIFICATO'
      : result.provisionalPattern
        ? 'NON CONFERMATO'
        : 'NON CLASSIFICATO'

  const refreshEpisodes = async () => {
    const all = await listEpisodes()
    setEpisodes(all)
    return all
  }

  useEffect(() => {
    Promise.all([loadSelectedEpisode(), listEpisodes()])
      .then(([loaded, all]) => {
        setEpisodes(all)
        if (loaded) {
          setEpisode(loaded)
          setRestored(true)
        }
      })
      .catch(() => setStorageError('Impossibile accedere all’archivio locale dei casi su questo dispositivo.'))
      .finally(() => setEpisodeLoaded(true))
  }, [])

  const handleFormMutation = () => {
    if (!formRef.current) return
    setCanAnalyze(hasAnalysisData(new FormData(formRef.current)))
    if (hasAnalyzed) { setStale(true); setSaved(false) }
  }

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const fd = new FormData(event.currentTarget)
    if (!hasAnalysisData(fd)) return
    setInput({
      knownDiabetes: fd.get('knownDiabetes') === 'on',
      sglt2Inhibitor: fd.get('sglt2Inhibitor') === 'on',
      frailty: fd.get('frailty') === 'on',
      weightKg: num(fd.get('weightKg')),
      glucoseMgDl: num(fd.get('glucoseMgDl')),
      sodiumMmolL: num(fd.get('sodiumMmolL')),
      potassiumMmolL: num(fd.get('potassiumMmolL')),
      chlorideMmolL: num(fd.get('chlorideMmolL')),
      bicarbonateMmolL: num(fd.get('bicarbonateMmolL')),
      ph: num(fd.get('ph')),
      bloodGasSample: (fd.get('bloodGasSample') as ClinicalInput['bloodGasSample']) || 'venous',
      betaHydroxybutyrateMmolL: num(fd.get('betaHydroxybutyrateMmolL')),
      urineKetones: urineKetones(fd.get('urineKetones')),
      ureaMmolL: num(fd.get('ureaMmolL')),
      heartFailure: fd.get('heartFailure') === 'on',
      advancedKidneyDisease: fd.get('advancedKidneyDisease') === 'on',
      dialysis: fd.get('dialysis') === 'on',
      pregnancy: fd.get('pregnancy') === 'on',
      mentalStatus: (fd.get('mentalStatus') as ClinicalInput['mentalStatus']) || 'unknown'
    })
    setHasAnalyzed(true)
    setStale(false)
    setSaved(false)
    window.setTimeout(() => {
      if (window.matchMedia('(max-width: 980px)').matches) resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 80)
  }

  const saveEvaluation = async () => {
    if (!hasAnalyzed || stale) return
    await saveLocalEvaluation({ version: '0.5.1', input, result })
    setSaved(true)
  }

  const startEpisode = async () => {
    if (!hasAnalyzed || stale || episode) return
    const initial = makeObservation(input)
    const next = createEpisode(initial)
    setEpisode(next)
    await saveEpisode(next)
    await refreshEpisodes()
    window.setTimeout(() => episodeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80)
  }

  const updateEpisode = (next: Episode) => {
    setEpisode(next)
    setEpisodes((current) => current.map((stored) => stored.id === next.id ? { ...stored, ...next, updatedAt: new Date().toISOString() } : stored))
    setRestored(false)
  }

  const startNewCase = () => {
    markNewCaseSelected()
    setEpisode(null)
    setInput({ knownDiabetes: false })
    setCanAnalyze(false)
    setHasAnalyzed(false)
    setStale(false)
    setSaved(false)
    setRestored(false)
    setArchiveOpen(false)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const openStoredEpisode = (selected: StoredEpisode) => {
    markEpisodeSelected(selected.id)
    setEpisode(selected)
    setRestored(false)
    setArchiveOpen(false)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const toggleArchive = () => {
    if (!archiveOpen) {
      void refreshEpisodes().catch(() => setStorageError('Impossibile aggiornare l’archivio locale dei casi.'))
    }
    setArchiveOpen((value) => !value)
  }

  return (
    <main className="shell">
      <header className="topbar">
        <div className="header-copy">
          <div className="header-meta"><p className="eyebrow">v0.5.1 · prototipo clinico</p><span className="demo-badge">SOLO DATI DEMO</span></div>
          <h1>Emergenze metaboliche diabetiche</h1>
          <p className="subtitle">Percorso progressivo: inquadramento · stabilizzazione · monitoraggio · risoluzione · transizione EV → SC</p>
        </div>
      </header>

      <section className="notice"><strong>Supporto decisionale in sviluppo.</strong> Non sostituisce valutazione clinica, protocolli locali o prescrizione medica. Non inserire dati identificativi reali.</section>
      {storageError && <section className="notice"><strong>Archivio locale.</strong> {storageError}</section>}

      {!episodeLoaded && <div className="restored-note">Caricamento dello stato locale…</div>}

      {episodeLoaded && (
        <CaseArchivePanel
          episodes={episodes}
          currentEpisode={episode}
          open={archiveOpen}
          onToggle={toggleArchive}
          onNewCase={startNewCase}
          onOpenEpisode={openStoredEpisode}
        />
      )}

      {episodeLoaded && episode && (
        <div ref={episodeRef} className="episode-slot">
          {restored && <div className="restored-note">Caso selezionato ripristinato dal dispositivo.</div>}
          <GuidedWorkflowPanel episode={episode} />
          <details className="episode-detail-shell" id="dettagli-episodio">
            <summary>Dettagli episodio · timeline · terapie registrate · correzioni</summary>
            <EpisodePanel episode={episode} onChange={updateEpisode} />
            <ObservationCorrectionPanel episode={episode} onChange={updateEpisode} />
          </details>
        </div>
      )}

      {episodeLoaded && !episode && (
        <div className="layout initial-layout">
          <form ref={formRef} className="panel input-panel" onSubmit={onSubmit} onInput={handleFormMutation} onChange={handleFormMutation}>
            <div className="panel-heading"><div><p className="step">Fase 1 di 5 · Inquadramento</p><h2>Dati essenziali</h2></div></div>
            <div className="grid fields-grid">
              <Field name="weightKg" label="Peso" unit="kg" optional />
              <Field name="glucoseMgDl" label="Glicemia" unit="mg/dL" needed />
              <Field name="sodiumMmolL" label="Na" unit="mmol/L" needed />
              <Field name="potassiumMmolL" label="K" unit="mmol/L" needed />
              <Field name="chlorideMmolL" label="Cl" unit="mmol/L" optional />
              <Field name="bicarbonateMmolL" label="HCO₃⁻" unit="mmol/L" needed />
              <Field name="ph" label="pH" step="0.01" needed />
              <label className="field"><span>Campione pH</span><select name="bloodGasSample" defaultValue="venous"><option value="venous">Venoso</option><option value="arterial">Arterioso</option></select></label>
              <Field name="betaHydroxybutyrateMmolL" label="β-idrossibutirrato" unit="mmol/L" optional />
              <Field name="ureaMmolL" label="Urea" unit="mmol/L" optional />
              <label className="field urine-field"><span className="field-label"><span>Chetonuria</span><small>fallback</small></span><select name="urineKetones" defaultValue=""><option value="">Non disponibile / non eseguita</option><option value="0">Negativi</option><option value="0.5">Tracce</option><option value="1">1+</option><option value="2">2+</option><option value="3">3+</option><option value="4">4+</option></select></label>
            </div>
            <p className="form-hint"><strong>*</strong> necessari per l’analisi di base. β-OHB e chetonuria sono fortemente raccomandati ma non bloccano il tool: se entrambi mancano, DKA/HHS restano non confermate e non viene proposta una velocità insulinica automatica. Il pH può essere venoso o arterioso, senza conversioni automatiche.</p>
            <div className="subsection">
              <h3>Contesto clinico</h3>
              <div className="check-grid">
                <label><input type="checkbox" name="knownDiabetes" /> Diabete noto</label><label><input type="checkbox" name="sglt2Inhibitor" /> SGLT2-inibitore</label>
                <label><input type="checkbox" name="heartFailure" /> Scompenso cardiaco</label><label><input type="checkbox" name="advancedKidneyDisease" /> IRC avanzata</label>
                <label><input type="checkbox" name="dialysis" /> Dialisi</label><label><input type="checkbox" name="pregnancy" /> Gravidanza</label><label><input type="checkbox" name="frailty" /> Fragilità / età avanzata</label>
              </div>
              <label className="field select-field"><span>Stato neurologico</span><select name="mentalStatus" defaultValue="unknown"><option value="unknown">Non specificato</option><option value="alert">Vigile</option><option value="drowsy">Sonnolento</option><option value="stupor">Stupor</option><option value="coma">Coma</option></select></label>
            </div>
            <button className="primary" type="submit" disabled={!canAnalyze}>{stale ? 'Rianalizza' : 'Analizza'}</button>
            {!canAnalyze && <p className="button-hint">Completa glicemia, Na, K, HCO₃⁻ e pH per attivare l’analisi di base.</p>}
          </form>

          <section ref={resultRef} className={`panel result-panel ${!hasAnalyzed ? 'pre-analysis' : ''}`} aria-live="polite">
            <div className="panel-heading result-heading"><div><p className="step">Esito dell’inquadramento</p><h2>{resultTitle}</h2></div><span className={`status ${hasAnalyzed && result.diagnosis !== 'UNCLASSIFIED' ? 'active' : 'neutral'}`}>{resultStatus}</span></div>
            {!hasAnalyzed ? <div className="pre-analysis-hint"><strong>Compila i dati essenziali e premi Analizza.</strong><p>Il risultato comparirà qui con diagnosi o pattern non confermato, priorità e criteri diagnostici. Le indicazioni terapeutiche progressive inizieranno dopo “Avvia episodio”.</p></div> : <>
              {stale && <div className="stale-banner"><strong>Dati modificati.</strong> Premi Rianalizza per aggiornare il risultato.</div>}
              {result.provisionalPattern && <div className="stale-banner"><strong>{result.provisionalPattern.label}</strong><p>{result.provisionalPattern.message}</p></div>}
              <section className="priority-section"><p className="section-label">Priorità</p>{sortedAlerts.length ? <div className="alerts priority-alerts">{sortedAlerts.map((alert) => <article className={`alert ${alert.level}`} key={alert.id}><strong>{alert.title}</strong><details className="why"><summary>Perché?</summary><p>{alert.message}</p></details></article>)}</div> : <div className="no-priority">Nessun alert prioritario generato con i dati attuali.</div>}</section>
              {(result.dkaSeverity || result.insulin.suggestedUnitsHour !== undefined) && <div className="summary-grid">{result.dkaSeverity && <div className="clinical-card emphasis-card"><span>Fascia prudenziale DKA</span><strong>{severityLabels[result.dkaSeverity.band]}</strong><small>{result.dkaSeverity.reasons.join(' · ') || result.dkaSeverity.note}</small></div>}{result.insulin.suggestedUnitsHour !== undefined && <div className="clinical-card emphasis-card"><span>Insulina EV · riferimento iniziale</span><strong>{result.insulin.suggestedUnitsHour} U/h</strong><small>{result.insulin.suggestedUnitsKgHour} U/kg/h · il percorso terapeutico completo compare dopo l’avvio dell’episodio</small></div>}</div>}
              <p className="section-label metrics-label">Calcoli</p><div className="metric-grid"><div className="metric"><span>AG</span><strong>{result.calculations.anionGap ?? '—'}</strong><small>mmol/L</small></div><div className="metric"><span>Osm eff.</span><strong>{result.calculations.effectiveOsmolality ?? '—'}</strong><small>mOsm/kg</small></div><div className="metric"><span>Osm totale</span><strong>{result.calculations.totalOsmolality ?? '—'}</strong><small>mOsm/kg</small></div></div>
              <details className="criteria-details"><summary>Criteri diagnostici DKA / HHS</summary><div className="criteria-wrap"><CriterionGroup title="DKA" criteria={[result.dka.D, result.dka.K, result.dka.A]} /><CriterionGroup title="HHS" criteria={[result.hhs.G, result.hhs.O, result.hhs.K, result.hhs.A]} /></div></details>
              <RuleReferences ids={['diagnosis', 'ketones', 'insulin_iv']} />
              {!stale && <div className="result-actions"><button className="secondary" type="button" onClick={saveEvaluation}>Salva valutazione</button><button className="primary episode-start" type="button" onClick={startEpisode}>Avvia episodio e gestione</button></div>}
              {saved && <p className="saved">Valutazione salvata localmente.</p>}
            </>}
          </section>
        </div>
      )}

      {episodeLoaded && <details className="global-reference-shell"><summary>Fonti e metodologia</summary><SourcesMethodologyPanel /></details>}
      {episodeLoaded && <DemoScenarioPanel />}

      <footer>Motore deterministico · percorso clinico progressivo · fonti tracciate per regola · casi, dati e timeline locali IndexedDB · nessun dato inviato a servizi esterni nella v0.5.1</footer>
    </main>
  )
}
